import { isLeft } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import { DocmapsFactory } from './types';
import * as TE from 'fp-ts/lib/TaskEither';
import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import SerializerJsonld from '@rdfjs/serializer-jsonld-ext';
import * as util from 'util';
const DM_JSONLD_CONTEXT = 'https://w3id.org/docmaps/context.jsonld';
export const TypedNodeShape = t.type({
    type: t.union([t.string, t.array(t.string)]),
});
export const DocmapNormalizedFrame = {
    type: 'docmap',
    'first-step': { '@embed': '@never' },
    'pwo:hasStep': {
        '@embed': '@always',
        '@requireAll': false,
        'next-step': { '@embed': '@never', '@omitDefault': true },
        'previous-step': { '@embed': '@never', '@omitDefault': true },
        '@id': {},
    },
};
export class TypedGraph {
    constructor(factory = DocmapsFactory) {
        this.factory = factory;
    }
    parseJsonldWithCodec(c, jsonld) {
        const typedResult = c.decode(jsonld);
        if (isLeft(typedResult)) {
            throw new Error(`decoding failed: ${util.inspect(typedResult.left, { depth: 5 })}`);
        }
        return typedResult.right;
    }
    codecFor(jsonld) {
        let typesArr;
        if (Array.isArray(jsonld['type'])) {
            typesArr = jsonld['type'];
        }
        else {
            typesArr = [jsonld['type']];
        }
        const errors = [];
        for (const tIdStr of typesArr) {
            const tId = tIdStr;
            if (!tId) {
                errors.push(new Error(`unable to type a jsonld object without type field: ${JSON.stringify(jsonld, null, '  ')}`));
                continue;
            }
            const t = this.factory[tId];
            if (!t) {
                errors.push(new Error(`unable to type jsonld object: type \`${tId}\` is foreign to this type factory`));
                continue;
            }
            return t;
        }
        throw errors;
    }
    oneJsonldFrom(s, frame) {
        const context = {
            '@context': {
                '@import': DM_JSONLD_CONTEXT,
            },
            ...frame,
        };
        const serializer = new SerializerJsonld({
            context: context,
            frame: true,
            skipContext: true,
        });
        const output = serializer.import(s);
        return TE.tryCatch(() => new Promise((res, rej) => {
            let done = false;
            output
                .on('data', (jsonld) => {
                try {
                    done = true;
                    res(jsonld);
                }
                catch (errors) {
                    done = true;
                    rej(new Error('no type annotations were parseable for object', { cause: errors }));
                }
            });
            output.on('error', (err) => {
                done = true;
                rej(new Error('error received from upstream', { cause: err }));
            });
            output.on('end', () => {
                if (!done) {
                    rej(new Error('jsonld streaming exited unexpectedly'));
                }
            });
            output.on('close', () => {
                if (!done) {
                    rej(new Error('jsonld streaming exited unexpectedly'));
                }
            });
        }), (reason) => new Error('unable to pick jsonld from stream', { cause: reason }));
    }
    pickStreamWithCodec(frame, codec, s) {
        const te_ld = this.oneJsonldFrom(s, frame);
        const decodeWithError = E.mapLeft((errors) => new Error('failed to decode a docmap', { cause: errors }));
        return pipe(te_ld, TE.chainEitherK((ld) => pipe(ld, codec.decode, decodeWithError)));
    }
    pickStream(s, frame) {
        return pipe(TE.Do, TE.bind('jsonld', () => this.oneJsonldFrom(s, frame)), TE.bind('codec', ({ jsonld }) => TE.of(this.codecFor(jsonld))), TE.map(({ codec, jsonld }) => this.parseJsonldWithCodec(codec, jsonld)));
    }
}
//# sourceMappingURL=typed_graph.js.map